import type { PlanetPosition } from '../../types/api';
import {
  isInGandanta,
  computeMoonPhase,
  detectYutis,
  ZODIAC_SANSKRIT_NAMES,
  ZODIAC_NAMES,
} from './alignmentUtils';

// ── Types ───────────────────────────────────────────────────────────
export type InsightCategory =
  | 'retrograde'
  | 'gandanta'
  | 'conjunction'
  | 'dignity'
  | 'aspect'
  | 'transit'
  | 'moon'
  | 'chandrashtama';

export type InsightSeverity = 'info' | 'caution' | 'auspicious';

export interface Insight {
  category: InsightCategory;
  severity: InsightSeverity;
  title: string;
  description: string;
  effect: string;
}

// ── Reference Data ──────────────────────────────────────────────────

// Planets that are always retrograde (shadow planets) — skip in retrograde detector
const ALWAYS_RETROGRADE = new Set(['Rahu', 'Ketu']);

// Sanskrit planet names for insight titles
const PLANET_SANSKRIT: Record<string, string> = {
  Sun: 'Sūrya',
  Moon: 'Candra',
  Mars: 'Kuja',
  Mercury: 'Budha',
  Jupiter: 'Guru',
  Venus: 'Śukra',
  Saturn: 'Śani',
  Rahu: 'Rāhu',
  Ketu: 'Ketu',
};

// Karakatva — life domains governed by each planet
const KARAKATVA: Record<string, { domains: string; retroEffect: string; gandantaEffect: string }> = {
  Sun: {
    domains: 'authority, health, vitality',
    retroEffect: 'Self-doubt, leadership under review.',
    gandantaEffect: 'Identity crisis, health vulnerability.',
  },
  Moon: {
    domains: 'mind, emotions, nourishment',
    retroEffect: 'Emotional revisiting, nostalgia.',
    gandantaEffect: 'Deep emotional turbulence.',
  },
  Mars: {
    domains: 'energy, courage, property',
    retroEffect: 'Low drive, delayed actions.',
    gandantaEffect: 'Volatile energy, conflict crisis.',
  },
  Mercury: {
    domains: 'communication, intellect, commerce',
    retroEffect: 'Communication delays, miscommunication likely. Review contracts.',
    gandantaEffect: 'Intellectual confusion, speech difficulties.',
  },
  Jupiter: {
    domains: 'wisdom, dharma, prosperity',
    retroEffect: 'Internal growth, belief review period.',
    gandantaEffect: 'Spiritual crisis, faith tested.',
  },
  Venus: {
    domains: 'love, marriage, arts',
    retroEffect: 'Relationship re-evaluation, past connections resurface.',
    gandantaEffect: 'Desire/detachment conflict.',
  },
  Saturn: {
    domains: 'karma, career, discipline',
    retroEffect: 'Karmic review, delayed career results. Patience needed.',
    gandantaEffect: 'Career upheaval, structural breakdown.',
  },
};

// Exaltation (Uccha) and Debilitation (Nicha) signs
const DIGNITY_TABLE: Record<string, { uccha: number; nicha: number }> = {
  Sun: { uccha: 1, nicha: 7 },       // Aries / Libra
  Moon: { uccha: 2, nicha: 8 },      // Taurus / Scorpio
  Mars: { uccha: 10, nicha: 4 },     // Capricorn / Cancer
  Mercury: { uccha: 6, nicha: 12 },  // Virgo / Pisces
  Jupiter: { uccha: 4, nicha: 10 },  // Cancer / Capricorn
  Venus: { uccha: 12, nicha: 6 },    // Pisces / Virgo
  Saturn: { uccha: 7, nicha: 1 },    // Libra / Aries
};

// All planets aspect 7th (180°). Mars, Jupiter, Saturn have additional aspects.
// Rāhu/Ketu take the 5th and 9th, as in the yuti engine.
// Graha dṛṣṭi beyond the universal 7th, as house counts from the graha's own
// rāśi (inclusive). Mirrors src/jyotisha_core/aspects.py, which the yuti
// engine and the narrative detector use; counted by rāśi, never by degree
// orb (B-138: 19% of orb-based "4th" labels missed the 4th rāśi).
const SPECIAL_DRISHTI_HOUSES: Record<string, number[]> = {
  Mars: [4, 8],
  Jupiter: [5, 9],
  Saturn: [3, 10],
  Rahu: [5, 9],
  Ketu: [5, 9],
};

const ORDINAL: Record<number, string> = { 3: '3rd', 4: '4th', 5: '5th', 8: '8th', 9: '9th', 10: '10th' };

const houseFrom = (fromRashi: number, toRashi: number): number =>
  (((toRashi - fromRashi) % 12) + 12) % 12 + 1;

// Natural benefics and malefics
const NATURAL_MALEFICS = new Set(['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu']);

// Conjunction special names (yogas)
const CONJUNCTION_YOGAS: Record<string, string> = {
  'Sun-Mercury': 'Budha-Āditya Yoga — sharp intellect, strong communication.',
  'Jupiter-Venus': 'Guru-Śukra Yuti — wisdom meets creativity. Auspicious.',
  'Sun-Saturn': 'Tension between authority and discipline.',
  'Mars-Saturn': 'Intense energy meets restraint. Exercise caution.',
  'Jupiter-Saturn': 'Expansion meets contraction. Balanced growth.',
  'Mars-Rahu': 'Aṅgāraka Yoga — impulsive energy amplified. Caution advised.',
  'Moon-Rahu': 'Grahan Yoga — emotional eclipsing. Avoid major decisions.',
  'Moon-Ketu': 'Spiritual detachment from emotions. Inner contemplation.',
  'Sun-Rahu': 'Authority challenged by unconventional forces.',
  'Sun-Ketu': 'Ego dissolution, spiritual insight.',
  'Venus-Saturn': 'Challenges in relationships. Patience in love.',
  'Mercury-Saturn': 'Slow, methodical thinking. Delays in communication.',
  'Mars-Jupiter': 'Courageous wisdom. Favorable for bold dharmic action.',
  'Venus-Mars': 'Passion intensified. Creative and romantic energy.',
  'Mercury-Jupiter': 'Expansive intellect. Favorable for learning.',
};

// ── Detector Functions ──────────────────────────────────────────────

/**
 * Detector 1: Retrograde planets (skip Rahu/Ketu — always retrograde)
 */
export const detectRetrogrades = (planets: PlanetPosition[]): Insight[] => {
  return planets
    .filter((p) => p.motion === 'RETROGRADE' && !ALWAYS_RETROGRADE.has(p.planet_english))
    .map((p) => {
      const sanskrit = PLANET_SANSKRIT[p.planet_english] || p.planet_english;
      const karaka = KARAKATVA[p.planet_english];
      const rashiSanskrit = ZODIAC_SANSKRIT_NAMES[p.zodiac_sign_number] || p.zodiac_sign;
      return {
        category: 'retrograde' as const,
        severity: 'caution' as const,
        title: `Vakri ${sanskrit}`,
        description: `${p.planet_english} retrograde in ${rashiSanskrit} (${p.zodiac_sign})`,
        effect: karaka?.retroEffect || `${karaka?.domains || p.planet_english} under review.`,
      };
    });
};

/**
 * Detector 2: Planets in Gandanta zones (water-fire sign boundaries)
 */
export const detectGandantaPlanets = (planets: PlanetPosition[]): Insight[] => {
  return planets
    .filter((p) => isInGandanta(p.longitude_degrees))
    .map((p) => {
      const sanskrit = PLANET_SANSKRIT[p.planet_english] || p.planet_english;
      const karaka = KARAKATVA[p.planet_english];
      return {
        category: 'gandanta' as const,
        severity: 'caution' as const,
        title: `${sanskrit} in Gandānta`,
        description: `${p.planet_english} at ${p.degrees_in_sign_dms.degrees}°${String(p.degrees_in_sign_dms.minutes).padStart(2, '0')}' in ${p.zodiac_sign}`,
        effect: karaka?.gandantaEffect || `Transformative period for ${karaka?.domains || 'related matters'}.`,
      };
    });
};

/**
 * Detector 2b: Moon approaching Gandanta
 * The Moon moves ~13°/day, so it traverses the 6.66° gandanta zone in ~12 hours.
 * Daily snapshots at 00:00 UTC often miss it entirely.
 * This detector flags when the Moon is within ~10° of entering a gandanta zone,
 * giving roughly 18-24 hours of advance warning.
 */
const GANDANTA_WATER_SIGNS = new Set([4, 8, 12]); // Cancer, Scorpio, Pisces
const MOON_APPROACH_DEG = 10; // degrees before gandanta zone
const GANDANTA_ZONE_START_DEG = 30 - 3.33; // 26.67° — where gandanta begins in water sign

export const detectApproachingMoonGandanta = (planets: PlanetPosition[]): Insight[] => {
  const moon = planets.find((p) => p.planet_english === 'Moon');
  if (!moon) return [];

  // Only check if Moon is in a water sign with gandanta boundary at its end
  if (!GANDANTA_WATER_SIGNS.has(moon.zodiac_sign_number)) return [];

  // Skip if already in gandanta (caught by detectGandantaPlanets)
  if (isInGandanta(moon.longitude_degrees)) return [];

  // Check if approaching: degrees_in_sign between (26.67 - 10) and 26.67
  const approachStart = GANDANTA_ZONE_START_DEG - MOON_APPROACH_DEG;
  if (moon.degrees_in_sign >= approachStart && moon.degrees_in_sign < GANDANTA_ZONE_START_DEG) {
    const currentEnglish = ZODIAC_NAMES[moon.zodiac_sign_number] || moon.zodiac_sign;
    const nextSignNum = (moon.zodiac_sign_number % 12) + 1;
    const nextEnglish = ZODIAC_NAMES[nextSignNum] || '';

    return [{
      category: 'gandanta' as const,
      severity: 'caution' as const,
      title: 'Candra Gandānta approaching',
      description: `Moon approaching gandanta at ${currentEnglish}\u2013${nextEnglish} boundary`,
      effect: 'Deep emotional turbulence building. Exercise increasing caution, avoid major decisions.',
    }];
  }

  return [];
};

/**
 * Detector 3: Conjunction insights (classical rāśi co-tenancy).
 * Grouping delegated to detectYutis (alignmentUtils) — single source of truth for both
 * the wheel labels and the insight panel.
 */
export const detectConjunctionInsights = (planets: PlanetPosition[]): Insight[] => {
  return detectYutis(planets).map(({ planets: group, signNumber }) => {
    const names = group.map((p) => p.planet_english).sort();
    const sanskritNames = names.map((n) => PLANET_SANSKRIT[n] || n);
    const rashiSanskrit = ZODIAC_SANSKRIT_NAMES[signNumber] || '';
    const rashiEnglish = ZODIAC_NAMES[signNumber] || '';
    const longitudes = group.map((p) => p.longitude_degrees);
    const spreadDegrees = Math.max(...longitudes) - Math.min(...longitudes);

    let yogaEffect = '';
    for (let i = 0; i < names.length; i++) {
      for (let j = i + 1; j < names.length; j++) {
        const key = `${names[i]}-${names[j]}`;
        if (CONJUNCTION_YOGAS[key]) { yogaEffect = CONJUNCTION_YOGAS[key]; break; }
      }
      if (yogaEffect) break;
    }

    const isMaleficHeavy = group.filter((p) => NATURAL_MALEFICS.has(p.planet_english)).length >= 2;
    const severity: InsightSeverity = yogaEffect?.includes('Auspicious') || yogaEffect?.includes('Favorable')
      ? 'auspicious'
      : isMaleficHeavy ? 'caution' : 'info';

    return {
      category: 'conjunction' as const,
      severity,
      title: `Yuti: ${sanskritNames.join('–')}`,
      description: `${names.join(', ')} conjunction in ${rashiSanskrit} (${rashiEnglish}), ${spreadDegrees.toFixed(1)}° spread`,
      effect: yogaEffect || `Combined energies affect ${group.map((p) => KARAKATVA[p.planet_english]?.domains || '').filter(Boolean).join(', ')}.`,
    };
  });
};

/**
 * Detector 4: Planetary dignity — exaltation (uccha) and debilitation (nicha)
 */
export const detectDignity = (planets: PlanetPosition[]): Insight[] => {
  const insights: Insight[] = [];
  for (const p of planets) {
    const dignity = DIGNITY_TABLE[p.planet_english];
    if (!dignity) continue;
    const sanskrit = PLANET_SANSKRIT[p.planet_english] || p.planet_english;
    const karaka = KARAKATVA[p.planet_english];
    const rashiSanskrit = ZODIAC_SANSKRIT_NAMES[p.zodiac_sign_number] || p.zodiac_sign;

    if (p.zodiac_sign_number === dignity.uccha) {
      insights.push({
        category: 'dignity',
        severity: 'auspicious',
        title: `Uccha ${sanskrit}`,
        description: `${p.planet_english} exalted in ${rashiSanskrit} (${p.zodiac_sign})`,
        effect: `${karaka?.domains || p.planet_english} strengthened. Favorable period.`,
      });
    } else if (p.zodiac_sign_number === dignity.nicha) {
      insights.push({
        category: 'dignity',
        severity: 'caution',
        title: `Nīca ${sanskrit}`,
        description: `${p.planet_english} debilitated in ${rashiSanskrit} (${p.zodiac_sign})`,
        effect: `${karaka?.domains || p.planet_english} weakened. Challenges likely.`,
      });
    }
  }
  return insights;
};

/**
 * Detector 5: Jyotish aspects (drishti), counted by rāśi.
 * The universal 7th is true for every graha but not listed: it would add a
 * line for nearly every pair. Special aspects only.
 */
export const detectAspects = (planets: PlanetPosition[]): Insight[] => {
  const insights: Insight[] = [];

  for (const aspector of planets) {
    const houses = SPECIAL_DRISHTI_HOUSES[aspector.planet_english];
    if (!houses) continue;

    for (const house of houses) {
      for (const target of planets) {
        if (target.planet_english === aspector.planet_english) continue;
        if (houseFrom(aspector.zodiac_sign_number, target.zodiac_sign_number) !== house) continue;

        const aspSanskrit = PLANET_SANSKRIT[aspector.planet_english] || aspector.planet_english;
        const tgtSanskrit = PLANET_SANSKRIT[target.planet_english] || target.planet_english;
        const isMalefic = NATURAL_MALEFICS.has(aspector.planet_english);

        insights.push({
          category: 'aspect',
          severity: isMalefic ? 'caution' : 'auspicious',
          title: `${aspSanskrit} dṛṣṭi on ${tgtSanskrit}`,
          description: `${aspector.planet_english}'s ${ORDINAL[house]} aspect on ${target.planet_english}`,
          effect: isMalefic
            ? `Pressure on ${KARAKATVA[target.planet_english]?.domains || target.planet_english}. Stay mindful.`
            : `Support for ${KARAKATVA[target.planet_english]?.domains || target.planet_english}. Favorable influence.`,
        });
      }
    }
  }
  return insights;
};

/**
 * Detector 6: Transit proximity — planet within 1° of sign boundary (rashi sandhi)
 */
export const detectTransitProximity = (planets: PlanetPosition[]): Insight[] => {
  const SANDHI_THRESHOLD = 1.0; // degrees
  return planets
    .filter((p) => {
      // Skip Rahu/Ketu — they move very slowly across boundaries
      if (ALWAYS_RETROGRADE.has(p.planet_english)) return false;
      const degInSign = p.degrees_in_sign;
      return degInSign < SANDHI_THRESHOLD || degInSign > (30 - SANDHI_THRESHOLD);
    })
    .map((p) => {
      const sanskrit = PLANET_SANSKRIT[p.planet_english] || p.planet_english;
      const karaka = KARAKATVA[p.planet_english];
      const isLeaving = p.degrees_in_sign > (30 - SANDHI_THRESHOLD);
      const currentSign = ZODIAC_NAMES[p.zodiac_sign_number] || p.zodiac_sign;
      const nextSignNum = isLeaving ? (p.zodiac_sign_number % 12) + 1 : p.zodiac_sign_number;
      const prevSignNum = !isLeaving ? ((p.zodiac_sign_number - 2 + 12) % 12) + 1 : p.zodiac_sign_number;
      const transitionSign = isLeaving ? ZODIAC_NAMES[nextSignNum] : ZODIAC_NAMES[prevSignNum];

      return {
        category: 'transit' as const,
        severity: 'info' as const,
        title: `${sanskrit} Rāśi Sandhi`,
        description: `${p.planet_english} at sign boundary: ${currentSign}${isLeaving ? ` → ${transitionSign}` : ` (just entered from ${transitionSign})`}`,
        effect: `Shift in ${karaka?.domains || 'related matters'}. Transition period.`,
      };
    });
};

/**
 * Detector 7: Moon phase insights — notable phases and eclipse proximity
 */
export const detectMoonPhaseInsight = (planets: PlanetPosition[], tithiStr?: string): Insight[] => {
  const sun = planets.find((p) => p.planet_english === 'Sun');
  const moon = planets.find((p) => p.planet_english === 'Moon');
  if (!sun || !moon) return [];

  const phase = computeMoonPhase(sun.longitude_degrees, moon.longitude_degrees);
  const insights: Insight[] = [];

  // Use panchanga tithi if available, otherwise fall back to astronomical approximation.
  // Only the tithi in force at sunrise counts: 'caturdaśī till 21:36, Amāvāsyā
  // afterwards' is Caturdaśī. Matching the whole string fired a day early (B-138).
  const tithiLower = (tithiStr || '').split(' till ')[0].split(',')[0].toLowerCase();
  const isAmavasya = tithiLower.includes('amāvāsyā') || tithiLower.includes('amavasya') || tithiLower.includes('amāvāsya');
  const isPurnima = tithiLower.includes('pūrṇimā') || tithiLower.includes('purnima') || tithiLower.includes('pūrnimā');
  const isNewMoon = tithiStr ? isAmavasya : phase.phaseName === 'New Moon';
  const isFullMoon = tithiStr ? isPurnima : phase.phaseName === 'Full Moon';

  // Notable phases
  if (isNewMoon || isFullMoon) {
    insights.push({
      category: 'moon',
      severity: isFullMoon ? 'info' : 'caution',
      title: isFullMoon ? 'Pūrṇimā' : 'Amāvāsyā',
      description: `${isFullMoon ? 'Full Moon' : 'New Moon'} — Moon ${Math.round(phase.illumination * 100)}% illuminated`,
      effect: isFullMoon
        ? 'Heightened emotions, clarity. Good for reflection and completion.'
        : 'Introspective energy. Avoid new beginnings. Suitable for meditation.',
    });
  }

  // Eclipse proximity: check if Rahu or Ketu is near the Sun-Moon axis
  const rahu = planets.find((p) => p.planet_english === 'Rahu');
  const ketu = planets.find((p) => p.planet_english === 'Ketu');
  if (rahu && (isNewMoon || isFullMoon)) {
    let rahuMoonDiff = Math.abs(rahu.longitude_degrees - moon.longitude_degrees);
    if (rahuMoonDiff > 180) rahuMoonDiff = 360 - rahuMoonDiff;
    let ketuMoonDiff = ketu ? Math.abs(ketu.longitude_degrees - moon.longitude_degrees) : 999;
    if (ketuMoonDiff > 180) ketuMoonDiff = 360 - ketuMoonDiff;

    if (rahuMoonDiff < 15 || ketuMoonDiff < 15) {
      insights.push({
        category: 'moon',
        severity: 'caution',
        title: 'Grahan Sūchanā',
        description: `Eclipse proximity — Rahu/Ketu near luminaries`,
        effect: 'Avoid major decisions. Heightened karmic intensity. Meditation recommended.',
      });
    }
  }

  return insights;
};

/**
 * Detector 8: Chandrashtama — Moon transiting 8th from a birth Moon sign
 * Shows which rashi's natives are affected today.
 */
export const detectChandrashtama = (planets: PlanetPosition[]): Insight[] => {
  const moon = planets.find((p) => p.planet_english === 'Moon');
  if (!moon) return [];

  const moonSign = moon.zodiac_sign_number; // 1-12
  // 8th from birth sign = current sign → birth sign = current - 7
  const affectedSign = ((moonSign - 7 - 1 + 12) % 12) + 1;
  const affectedSanskrit = ZODIAC_SANSKRIT_NAMES[affectedSign] || '';
  const affectedEnglish = ZODIAC_NAMES[affectedSign] || '';
  const moonRashi = ZODIAC_SANSKRIT_NAMES[moonSign] || '';

  return [{
    category: 'chandrashtama',
    severity: 'caution',
    title: 'Chandrāṣṭama',
    description: `Moon in ${moonRashi} — 8th transit for ${affectedSanskrit} (${affectedEnglish}) natives`,
    effect: 'Exercise caution, avoid major decisions. Emotional sensitivity heightened.',
  }];
};

// ── Orchestrator ────────────────────────────────────────────────────

export const generateInsights = (planets: PlanetPosition[], tithiStr?: string): Insight[] => {
  return [
    ...detectRetrogrades(planets),
    ...detectGandantaPlanets(planets),
    ...detectApproachingMoonGandanta(planets),
    ...detectConjunctionInsights(planets),
    ...detectDignity(planets),
    ...detectAspects(planets),
    ...detectTransitProximity(planets),
    ...detectMoonPhaseInsight(planets, tithiStr),
    ...detectChandrashtama(planets),
  ].slice(0, 12);
};

/**
 * Generate a compact 1-3 sentence summary from the top insights.
 * Picks the most significant insights (caution first, then auspicious, then info).
 */
export const generateSummary = (insights: Insight[]): string[] => {
  if (insights.length === 0) return [];

  // Priority: caution > auspicious > info
  const prioritized = [...insights].sort((a, b) => {
    const order: Record<string, number> = { caution: 0, auspicious: 1, info: 2 };
    return (order[a.severity] ?? 2) - (order[b.severity] ?? 2);
  });

  // Take top 3 and build concise sentences
  return prioritized.slice(0, 3).map((i) => {
    // Combine title + effect into a compact sentence
    return `${i.title}: ${i.effect}`;
  });
};
